// @ts-nocheck
// Cart price validator
//
// Custom sticker prices are non-linear in quantity (price table + discounts), so
// when a line item's quantity changes in the cart the stored `_customPrice`
// property becomes stale. `_customPrice` is read by the server-side Cart Transform
// function to set each line's price, so a stale value means a wrong charge.
//
// This validator monkey-patches `window.fetch` and, on any `/cart/change.js`
// request, recomputes `_customPrice` for the new quantity and injects it into the
// SAME outgoing request (pre-flight). A single request carries the new quantity +
// corrected price, so there's no extra round-trip, no flicker, and no double render.
//
// `_customPrice` format: per-piece dollars, 2 decimals (matches assets/sb-pdp.js).
//
// Depends on:
//   SBPriceCalculator               — assets/calculator.js
//   window.__SBProductMaterials  — snippets/calculator-scripts.liquid

(function () {
  "use strict";

  // The original fetch, captured once. Used ONLY for the terminal pass-through
  // call below and for the on-demand /cart.js read — both must bypass the patched
  // window.fetch to avoid recursion.
  const originalFetch = window.fetch.bind(window);

  /**
   * @param {RequestInfo | URL} input
   * @returns {boolean}
   */
  const isCartChange = (input) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input?.url || "";
    // Locale-safe: matches /cart/change.js and /<locale>/cart/change.js
    return url.includes("/cart/change");
  };

  /**
   * Recomputes _customPrice for the changed line and returns a new init with the
   * corrected properties injected. Returns the original init unchanged if the line
   * is not a custom sticker or the price is already correct.
   *
   * @param {RequestInit} init
   * @returns {Promise<RequestInit>}
   */
  const injectCustomPrice = async (init) => {
    console.log('[SB CartValidator] Started...', init);
    
    if (typeof init.body !== "string") return init;

    let payload = {};
    try {
      payload = JSON.parse(init.body);
    } catch (error) {
      payload = Object.fromEntries(new URLSearchParams(init.body));
    }

    const { line, quantity, id } = payload;

    console.log('[SB CartValidator]', payload);

    if (!(line || id) || !quantity) return init;

    // Read the line's current properties fresh (no snapshot to keep in sync).
    const cart = await originalFetch("/cart.js").then((r) => r.json());
    
    const lineIndex = id ? cart.items?.findIndex(i => i.key === id) : line - 1;

    const item = cart.items?.[lineIndex];

    if (!item) return init;

    const props = item.properties || {};

    // Only reprice custom stickers
    if (props._customSticker !== "true") return init;
    if (!props.Width || !props.Height || !props.Material) return init;

    const materials = window.__SBProductMaterials?.[item.handle];
    if (!Array.isArray(materials)) return init;

    const material = materials.find((m) => m.name === props.Material);
    if (!material?.cost || !material?.pricesByArea) return init;

    if (typeof SBPriceCalculator === "undefined") return init;

    const { finalPrice } = SBPriceCalculator.calculate({
      material,
      design: {
        widthIn: Number(props.Width),
        heightIn: Number(props.Height),
        quantity: Number(quantity),
      },
      extraMultiplier: Number(props._extraMultiplier) || 1,
    });

    // Per-piece dollars — prevents Shopify rounding errors at checkout.
    const newPerPiece = (finalPrice / quantity).toFixed(2);

    // Nothing to fix
    if (props._customPrice === newPerPiece) return init;

    // change.js REPLACES a line's properties, so send the full set.
    //
    // Build on whatever the caller was already sending, falling back to the
    // server's copy only when it sends none. `props` is a pre-request read, so
    // basing the merge on it would silently revert property edits the caller is
    // making in this very request — e.g. the cart's "Change Artwork" swapping the
    // Uploaded file URLs.
    const base = payload.properties ?? props;
    const newProps = { ...base, _customPrice: newPerPiece };

    // Keep the embedded _properties JSON aggregate consistent, if present.
    if (typeof base._properties === "string") {
      try {
        const aggregate = JSON.parse(base._properties);
        aggregate._customPrice = newPerPiece;
        newProps._properties = JSON.stringify(aggregate);
      } catch {
        // leave _properties as-is if it isn't valid JSON
      }
    }

    // Set headers
    let headers = init.headers;
    if (init.headers.length) {
      headers.push(['Content-Type', 'application/json']);
    } else {
      headers = {
        ...headers,
        'Content-Type': 'application/json',
      }
    }

    // Line ID changes after every cart/update request with properties change. We strip out the ID here and pass the fresh id to the payload.
    const {
      id: _id,
      ...restPayload
    } = payload;

    return {
      ...init,
      body: JSON.stringify({ 
        ...restPayload, 
        properties: newProps, 
        id: item.key 
      }),
      headers,
    };
  };

  window.fetch = async function (input, init) {
    try {
      if (init?.body && isCartChange(input)) {
        init = await injectCustomPrice(init);
      }
    } catch (error) {
      console.error("[SB CartValidator] failed to reprice line:", error);
      // fall through and send the original, unmodified request
    }

    return originalFetch(input, init);
  };
})();
